import React, { useCallback, useMemo, useState } from 'react';
import LanguageSelector from '../../../../../../components/LanguageSelector';
import { canViewAlarmReaction, canViewFeederScore, normalizeSuperuserCargo } from '../../../../../../features/ai-usage/aiUsageClient';
import TemporaryDrawer from '../../../../Components/Drawer/TemporaryDrawer';
import AnalyticsXlsxExportButton from '../../../../Components/utils/AnalyticsXlsxExportButton';
import LastMeasurement from '../../../../Components/utils/LastMeasurement';
import { SW_VERSION } from '../../../../../../version';
import { ItgMenuButton, LocationSelectorButton } from './components/DashboardSlots';
import useFeederScores from './hooks/useFeederScores';
import PanelRuntime from './PanelRuntime';
import { buildFullData } from './realData';
import { RECALC_STORAGE_KEY } from '../../FeederScore/recomputeFeederScore';
import './FullScappV2.css';

const readStoredScoreRecalcParams = () => {
  try {
    const stored = window.localStorage.getItem(RECALC_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (_error) {
    return null;
  }
};

function FullScappV2Skeleton() {
  return (
    <div className="scapp-full scapp-full-skeleton" role="status" aria-label="Cargando SCAPP versión 2">
      <div className="scapp-full-skeleton__header">
        <span className="scapp-full-skeleton__logo" />
        <span className="scapp-full-skeleton__language" />
        <span className="scapp-full-skeleton__title" />
        <span className="scapp-full-skeleton__measurement" />
        <span className="scapp-full-skeleton__brand" />
      </div>
      <div className="scapp-full-skeleton__content">
        <div className="scapp-full-skeleton__summary">
          {Array.from({ length: 4 }, (_, index) => <span key={index} />)}
        </div>
        <div className="scapp-full-skeleton__grid">
          <span className="scapp-full-skeleton__card scapp-full-skeleton__card--wide" />
          <span className="scapp-full-skeleton__card" />
          <span className="scapp-full-skeleton__card" />
          <span className="scapp-full-skeleton__card scapp-full-skeleton__card--wide" />
        </div>
      </div>
      <span className="scapp-full-skeleton__sr">Cargando datos del panel…</span>
    </div>
  );
}

/**
 * Compone la experiencia completa de SCAPP V2 y conecta sus adaptadores externos.
 *
 * La representación y la lógica analítica viven en PanelRuntime; este componente
 * coordina navegación, permisos, exportación y fuentes remotas de la página.
 */
export default function FullScappV2(props) {
  const {
    dailyData,
    data,
    dias,
    nivelSeleccionado,
    regionSeleccionada,
    areaSeleccionada,
    centroSeleccionado,
    setRegionSeleccionada,
    setAreaSeleccionada,
    setCentroSeleccionado,
    setNivelSeleccionado,
    permisosCentros,
    translate,
    idioma,
    compact,
    groupMode,
    servidor,
    loading,
    initialLoading,
    capitalize,
    setViewMode,
    selectedCenters,
    setSelectedCenters,
  } = props;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [xlsxExport, registerXlsxExport] = useState(null);
  const [scoreRecalcParams, setScoreRecalcState] = useState(readStoredScoreRecalcParams);
  const showInitialSkeleton = Boolean(initialLoading);
  const model = useMemo(
    () => buildFullData(dailyData, nivelSeleccionado, dias),
    [dailyData, nivelSeleccionado, dias],
  );
  const cargo = localStorage.getItem('cargo');
  const feederAllowed = canViewFeederScore(cargo);
  const insightAllowed = normalizeSuperuserCargo(cargo);
  const setScoreRecalcParams = useCallback(params => {
    setScoreRecalcState(params);
    try {
      if (params) window.localStorage.setItem(RECALC_STORAGE_KEY, JSON.stringify(params));
      else window.localStorage.removeItem(RECALC_STORAGE_KEY);
    } catch (_error) {}
  }, []);
  const score = useFeederScores({
    allowed: feederAllowed,
    level: nivelSeleccionado,
    region: regionSeleccionada,
    area: areaSeleccionada,
    center: centroSeleccionado,
    days: dias,
    server: servidor,
    recalcParams: scoreRecalcParams,
  });
  const clearXlsxExport = useCallback(
    id => registerXlsxExport(current => current?.id === id ? null : current),
    [],
  );
  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  const locationSlot = (
    <LocationSelectorButton
      level={nivelSeleccionado}
      name={props.nameVista}
      onOpen={openDrawer}
    />
  );
  const itgSlot = <ItgMenuButton onOpen={openDrawer} onClose={closeDrawer} />;

  return (
    <>
      {showInitialSkeleton ? <FullScappV2Skeleton /> : <PanelRuntime
        {...props}
        model={model}
        feederAllowed={feederAllowed}
        insightAllowed={insightAllowed}
        showReaction={canViewAlarmReaction(cargo)}
        scores={score.scores}
        scoreRecalcParams={scoreRecalcParams}
        setScoreRecalcParams={setScoreRecalcParams}
        scoreLoading={score.loading}
        scoreError={score.error}
        loadScores={score.reload}
        locationSlot={locationSlot}
        openLocation={openDrawer}
        itgSlot={itgSlot}
        languageSlot={(
          <LanguageSelector
            translate={translate}
            sx={{ marginRight: '-2.5vw', minWidth: '8.75vw' }}
          />
        )}
        measurementSlot={nivelSeleccionado === 'centro'
          ? (
            <LastMeasurement
              {...{ translate, idioma, regionSeleccionada, areaSeleccionada, centroSeleccionado }}
            />
          )
          : null}
        downloadData={() => xlsxExport?.run?.()}
      />}
      {loading && !showInitialSkeleton && (
        <div className="scapp-full-loading" role="status">
          Actualizando datos…
        </div>
      )}
      <AnalyticsXlsxExportButton
        data={data}
        dias={dias}
        nivelSeleccionado={nivelSeleccionado}
        groupMode={groupMode}
        compact={compact}
        selectedEntity={props.jaulaSeleccionada}
        selectedDay={props.selectedDay}
        region={regionSeleccionada}
        area={areaSeleccionada}
        center={centroSeleccionado}
        translate={translate}
        registerXlsxExport={registerXlsxExport}
        clearXlsxExport={clearXlsxExport}
      />
      <TemporaryDrawer
        open={drawerOpen}
        setOpen={setDrawerOpen}
        translate={translate}
        capitalize={capitalize}
        SWVersion={SW_VERSION}
        setViewMode={setViewMode}
        handleLogoMouseEnter={openDrawer}
        handleLogoMouseLeave={closeDrawer}
        permisosCentros={permisosCentros}
        nivelSeleccionado={nivelSeleccionado}
        setNivelSeleccionado={setNivelSeleccionado}
        regionSeleccionada={regionSeleccionada}
        setRegionSeleccionada={setRegionSeleccionada}
        areaSeleccionada={areaSeleccionada}
        setAreaSeleccionada={setAreaSeleccionada}
        centroSeleccionado={centroSeleccionado}
        setCentroSeleccionado={setCentroSeleccionado}
        selectedCenters={selectedCenters}
        setSelectedCenters={setSelectedCenters}
      />
    </>
  );
}
